// v2.1 finding #50 (Phase 4 rehearsal — Owner Console onboarding discovery):
// Pure validation helpers that mirror the server schema
// `createTeamMemberBodySchema` in
// [packages/api/src/routes/teamMemberRoutes.ts](../../packages/api/src/routes/teamMemberRoutes.ts).
// Pre-empt the opaque server "Validation failed" generic error by surfacing
// field-level issues before submit, and remap server `details.issues[]` back
// to per-field messages when the schema drifts ahead of the client.
//
// Kept as a pure module (zero React / DOM / network imports) so it can be
// unit-tested without rendering anything.

export const TEAM_MEMBER_PASSWORD_MIN_LENGTH = 8
export const TEAM_MEMBER_USERNAME_MIN_LENGTH = 3
export const TEAM_MEMBER_USERNAME_MAX_LENGTH = 40
// Mirrors the server-side username regex: letters, digits, dot, underscore,
// dash. Anchored so partial matches don't slip through.
export const TEAM_MEMBER_USERNAME_REGEX = /^[a-zA-Z0-9._-]+$/
// Lightweight email shape check — the server runs a stricter `z.string().email()`
// validation. The client check is intentionally permissive (one `@`, at least
// one dot in the domain, no whitespace) so we catch the obvious typos without
// re-implementing RFC 5322. If the server rejects, the mapped issue from
// `mapServerIssuesToFieldErrors` will still surface under the email field.
export const TEAM_MEMBER_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type TeamMemberFormField =
  | 'displayName'
  | 'email'
  | 'username'
  | 'password'
  | 'credentialsTrio'

export type TeamMemberFieldError = Readonly<{
  field: TeamMemberFormField
  message: string
}>

export type TeamMemberDraft = Readonly<{
  displayName: string
  email: string
  username: string
  password: string
}>

export type TeamMemberMessages = Readonly<{
  displayNameRequired: string
  emailInvalid: string
  usernameLength: string
  usernameFormat: string
  passwordTooShort: string
  credentialsTrio: string
}>

const hasContent = (value: string): boolean => value.trim().length > 0

/**
 * Run client-side validation that mirrors `createTeamMemberBodySchema` on
 * the server. Returns an empty list when the draft would pass — callers
 * should still expect the server to be the source of truth (e.g. unique
 * email/username constraints).
 */
export const validateTeamMemberDraft = (
  draft: TeamMemberDraft,
  messages: TeamMemberMessages
): TeamMemberFieldError[] => {
  const errors: TeamMemberFieldError[] = []

  if (!hasContent(draft.displayName)) {
    errors.push({ field: 'displayName', message: messages.displayNameRequired })
  }

  const email = draft.email.trim()
  if (email && !TEAM_MEMBER_EMAIL_REGEX.test(email)) {
    errors.push({ field: 'email', message: messages.emailInvalid })
  }

  const username = draft.username.trim()
  if (username) {
    if (
      username.length < TEAM_MEMBER_USERNAME_MIN_LENGTH
      || username.length > TEAM_MEMBER_USERNAME_MAX_LENGTH
    ) {
      errors.push({ field: 'username', message: messages.usernameLength })
    } else if (!TEAM_MEMBER_USERNAME_REGEX.test(username)) {
      errors.push({ field: 'username', message: messages.usernameFormat })
    }
  }

  const password = draft.password
  if (password && password.length < TEAM_MEMBER_PASSWORD_MIN_LENGTH) {
    errors.push({ field: 'password', message: messages.passwordTooShort })
  }

  // Trio rule from the server `superRefine` — `email + username + password`
  // must all be provided or all absent. Counts trimmed content per field so
  // whitespace-only inputs do not falsely satisfy the trio.
  const credentialCount = [email, username, password.trim()]
    .filter((value) => value.length > 0).length
  if (credentialCount !== 0 && credentialCount !== 3) {
    errors.push({ field: 'credentialsTrio', message: messages.credentialsTrio })
  }

  return errors
}

export type ServerValidationIssue = Readonly<{
  path: string
  message: string
  code?: string
}>

/**
 * Map server `details.issues[]` (from the Zod 400 envelope) back to per-field
 * errors so the form can surface the server's truth alongside (or after)
 * client-side checks. The server's `superRefine` trio rule emits with
 * `path: 'email'`; the message text is the only signal that it is a
 * cross-field error rather than a malformed email — detect it and remap to
 * `credentialsTrio`.
 */
export const mapServerIssuesToFieldErrors = (
  issues: ReadonlyArray<ServerValidationIssue>
): TeamMemberFieldError[] => {
  const out: TeamMemberFieldError[] = []
  for (const issue of issues) {
    const path = issue.path
    const message = issue.message

    if (path === 'email' && /provided together/i.test(message)) {
      out.push({ field: 'credentialsTrio', message })
      continue
    }

    if (path === 'displayName') out.push({ field: 'displayName', message })
    else if (path === 'email') out.push({ field: 'email', message })
    else if (path === 'username') out.push({ field: 'username', message })
    else if (path === 'password') out.push({ field: 'password', message })
    else out.push({ field: 'credentialsTrio', message: `${path}: ${message}` })
  }
  return out
}

/**
 * Best-effort extraction of `details.issues[]` from an `ApiError.body` shape.
 * The server wraps Zod errors as `{ error: 'Validation failed', details: {
 * issues: [...] } }`; the middleware also nests `details.location`. This
 * helper accepts both shapes and tolerates unknown payloads.
 */
export const extractValidationIssues = (body: unknown): ServerValidationIssue[] => {
  if (!body || typeof body !== 'object') return []
  const record = body as Record<string, unknown>
  const details = record.details
  if (!details || typeof details !== 'object') return []
  const issues = (details as Record<string, unknown>).issues
  if (!Array.isArray(issues)) return []
  const out: ServerValidationIssue[] = []
  for (const candidate of issues) {
    if (!candidate || typeof candidate !== 'object') continue
    const rec = candidate as Record<string, unknown>
    const path = typeof rec.path === 'string' ? rec.path : ''
    const message = typeof rec.message === 'string' ? rec.message : ''
    if (!message) continue
    const code = typeof rec.code === 'string' ? rec.code : undefined
    out.push({ path, message, code })
  }
  return out
}
